import { useState, useEffect } from "react";
import { Bookmark, Plus, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import { money } from "@/lib/landingSim";

const presetLabel = (p) => (p.kind === "fixed" ? `${p.page_count} pages = ${money(p.total)}` : p.pct < 0 ? `${-p.pct}% off` : `+${p.pct}% markup`);

export const applyPreset = (p) => (p.kind === "fixed"
  ? { page_count: p.page_count, price_tier: "custom", custom_unit_price: Math.round((p.total / p.page_count) * 100) / 100, markup_pct: 0, discount_pct: 0 }
  : p.pct < 0 ? { discount_pct: -p.pct, markup_pct: 0 } : { markup_pct: p.pct, discount_pct: 0 });

const NewPreset = ({ cfg, onCreated, onCancel }) => {
  const [f, setF] = useState({ name: "", kind: "percent", pct: -10, page_count: Number(cfg.page_count) || 10, total: "" });
  const save = async () => {
    try { const { data } = await api.post("/api/webadvisor/price-presets", { ...f, pct: Number(f.pct), page_count: Number(f.page_count), total: Number(f.total || 0) }); onCreated(data); }
    catch (err) { const d = err.response?.data?.detail; toast.error(Array.isArray(d) ? d.map(e => e.msg).join(", ") : d || "Couldn't save preset"); }
  };
  return (
    <div className="flex flex-wrap items-end gap-2 bg-slate-50 border border-slate-200 rounded-lg p-2.5" data-testid="preset-form">
      <Input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="Preset name" className="h-8 w-36 text-xs" data-testid="preset-name" />
      <select value={f.kind} onChange={e => setF({ ...f, kind: e.target.value })} className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white" data-testid="preset-kind">
        <option value="percent">Percent</option><option value="fixed">Fixed price</option>
      </select>
      {f.kind === "percent" ? (
        <label className="text-[11px] text-slate-500">% (− discount, + markup)<Input type="number" value={f.pct} onChange={e => setF({ ...f, pct: e.target.value })} className="h-8 w-24 text-xs" data-testid="preset-pct" /></label>
      ) : (<>
        <label className="text-[11px] text-slate-500">Pages<Input type="number" value={f.page_count} onChange={e => setF({ ...f, page_count: e.target.value })} className="h-8 w-20 text-xs" data-testid="preset-pages" /></label>
        <label className="text-[11px] text-slate-500">Package $<Input type="number" value={f.total} onChange={e => setF({ ...f, total: e.target.value })} className="h-8 w-28 text-xs" data-testid="preset-total" /></label>
      </>)}
      <button onClick={save} disabled={!f.name.trim()} className="h-8 text-xs font-bold bg-slate-900 text-white px-3 rounded-md disabled:opacity-50" data-testid="preset-save">Save preset</button>
      <button onClick={onCancel} className="h-8 text-xs text-slate-500 px-2" data-testid="preset-cancel">Cancel</button>
    </div>
  );
};

export const PricePresets = ({ cfg, onApply, ro }) => {
  const [presets, setPresets] = useState([]);
  const [adding, setAdding] = useState(false);
  useEffect(() => { api.get("/api/webadvisor/price-presets").then(r => setPresets(r.data)).catch(() => {}); }, []);
  const del = async (p) => { if (!window.confirm(`Delete preset "${p.name}"?`)) return; await api.delete(`/api/webadvisor/price-presets/${p.id}`); setPresets(ps => ps.filter(x => x.id !== p.id)); };
  if (ro && !presets.length) return null;
  return (
    <div className="space-y-2" data-testid="price-presets">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs font-semibold text-slate-500 flex items-center gap-1 mr-1"><Bookmark className="w-3.5 h-3.5" />Price presets</span>
        {presets.map(p => (
          <span key={p.id} className="inline-flex items-center bg-amber-50 border border-amber-200 rounded-full text-xs">
            <button disabled={ro} onClick={() => { onApply(applyPreset(p)); toast.success(`Applied "${p.name}"`); }} className="pl-2.5 pr-1.5 py-1 font-semibold text-amber-900 hover:text-amber-700" data-testid={`preset-apply-${p.id}`}>{p.name} <span className="font-normal text-amber-700">· {presetLabel(p)}</span></button>
            {!ro && <button onClick={() => del(p)} className="pr-2 text-amber-400 hover:text-red-600" data-testid={`preset-delete-${p.id}`}><X className="w-3 h-3" /></button>}
          </span>
        ))}
        {!ro && !adding && <button onClick={() => setAdding(true)} className="text-xs font-medium text-blue-600 hover:text-blue-500 flex items-center gap-0.5 px-1.5" data-testid="preset-add-btn"><Plus className="w-3.5 h-3.5" />New preset</button>}
      </div>
      {adding && <NewPreset cfg={cfg} onCancel={() => setAdding(false)} onCreated={(p) => { setPresets(ps => [...ps, p]); setAdding(false); toast.success("Preset saved"); }} />}
    </div>
  );
};
